import { collection, query, where, orderBy, getDocs, Timestamp } from 'firebase/firestore';
import { db } from './firebase';
import { TimeRecord, Project } from '@/types';
import { format, startOfMonth, endOfMonth, eachMonthOfInterval, eachDayOfInterval } from 'date-fns';
import { ja } from 'date-fns/locale';

export interface MonthlyStats {
  month: string;
  totalMinutes: number;
  totalHours: number;
  workDays: number;
  projectBreakdown: { [projectId: string]: number };
}

export interface ProjectStats {
  projectId: string;
  projectName: string;
  projectCode?: string;
  totalMinutes: number;
  totalHours: number;
  recordCount: number;
  percentage: number;
  totalAmount?: number;  // 報酬額
}

export interface DailyStats {
  date: string;
  totalMinutes: number;
  records: TimeRecord[];
}

/**
 * 期間内のタイムレコードを取得
 */
export async function fetchTimeRecordsForPeriod(
  userId: string,
  startDate: Date,
  endDate: Date
): Promise<TimeRecord[]> {
  const recordsRef = collection(db, 'users', userId, 'timestamps');
  const q = query(
    recordsRef,
    where('clockInTime', '>=', Timestamp.fromDate(startDate)),
    where('clockInTime', '<=', Timestamp.fromDate(endDate)),
    orderBy('clockInTime', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs
    .map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }))
    .filter((record: any) => record.status !== 'deleted') as TimeRecord[];
}

/**
 * 月次統計を計算
 */
export function calculateMonthlyStats(
  records: TimeRecord[],
  startDate: Date,
  endDate: Date
): MonthlyStats[] {
  const months = eachMonthOfInterval({ start: startDate, end: endDate });
  
  return months.map((month) => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    
    const monthRecords = records.filter((record) => {
      const recordDate = record.clockInTime.toDate();
      return recordDate >= monthStart && recordDate <= monthEnd;
    });

    const totalMinutes = monthRecords.reduce(
      (sum, record) => sum + (record.totalMinutes || 0),
      0
    );

    const workDays = new Set(
      monthRecords.map((record) =>
        format(record.clockInTime.toDate(), 'yyyy-MM-dd')
      )
    ).size;

    const projectBreakdown: { [key: string]: number } = {};
    monthRecords.forEach((record) => {
      const projectId = record.projectId || 'unassigned';
      projectBreakdown[projectId] = (projectBreakdown[projectId] || 0) + (record.totalMinutes || 0);
    });

    return {
      month: format(month, 'yyyy年M月', { locale: ja }),
      totalMinutes,
      totalHours: Math.round(totalMinutes / 60 * 10) / 10,
      workDays,
      projectBreakdown,
    };
  });
}

/**
 * プロジェクト別統計を計算
 */
export function calculateProjectStats(
  records: TimeRecord[],
  projects: Project[]
): ProjectStats[] {
  const totalMinutes = records.reduce((sum, record) => sum + (record.totalMinutes || 0), 0);
  
  const projectMap: { [key: string]: { minutes: number; count: number } } = {};
  
  records.forEach((record) => {
    const projectId = record.projectId || 'unassigned';
    if (!projectMap[projectId]) {
      projectMap[projectId] = { minutes: 0, count: 0 };
    }
    projectMap[projectId].minutes += record.totalMinutes || 0;
    projectMap[projectId].count += 1;
  });

  const stats: ProjectStats[] = Object.entries(projectMap).map(([projectId, data]) => {
    const project = projects.find((p) => p.id === projectId) || records.find((r) => r.projectId === projectId)?.project;
    
    return {
      projectId,
      projectName: project?.name || '不明なプロジェクト',
      projectCode: project?.code,
      totalMinutes: data.minutes,
      totalHours: Math.round(data.minutes / 60 * 10) / 10,
      recordCount: data.count,
      percentage: totalMinutes > 0 ? Math.round((data.minutes / totalMinutes) * 100) : 0,
    };
  });

  return stats.sort((a, b) => b.totalMinutes - a.totalMinutes);
}

/**
 * 日別統計を計算
 */
export function calculateDailyStats(
  records: TimeRecord[],
  startDate: Date,
  endDate: Date
): DailyStats[] {
  const days = eachDayOfInterval({ start: startDate, end: endDate });
  
  return days.map((day) => {
    const dayRecords = records.filter((record) => {
      const recordDate = format(record.clockInTime.toDate(), 'yyyy-MM-dd');
      const targetDate = format(day, 'yyyy-MM-dd');
      return recordDate === targetDate;
    });

    const totalMinutes = dayRecords.reduce(
      (sum, record) => sum + (record.totalMinutes || 0),
      0
    );

    return {
      date: format(day, 'yyyy-MM-dd'),
      totalMinutes,
      records: dayRecords,
    };
  });
}

/**
 * CSV形式にエクスポート
 */
export function exportToCSV(
  records: TimeRecord[],
  filename: string = 'timerecords.csv',
  selectedColumns?: string[]
): void {
  // 全ての利用可能な列定義
  const allColumns = {
    date: {
      header: '日付',
      getValue: (record: TimeRecord) => format(record.clockInTime.toDate(), 'yyyy-MM-dd'),
    },
    project: {
      header: 'プロジェクト',
      getValue: (record: TimeRecord) => record.project?.name || '',
    },
    projectCode: {
      header: 'プロジェクトコード',
      getValue: (record: TimeRecord) => record.project?.code || '',
    },
    client: {
      header: 'クライアント',
      getValue: (record: TimeRecord) => record.project?.client?.name || '',
    },
    startTime: {
      header: '開始時刻',
      getValue: (record: TimeRecord) => format(record.clockInTime.toDate(), 'HH:mm'),
    },
    endTime: {
      header: '終了時刻',
      getValue: (record: TimeRecord) =>
        record.clockOutTime ? format(record.clockOutTime.toDate(), 'HH:mm') : '',
    },
    actualHours: {
      header: '稼働時間（時間）',
      getValue: (record: TimeRecord) =>
        Math.round((record.totalMinutes || 0) / 60 * 100) / 100,
    },
    performance: {
      header: '実績時間（時間）',
      getValue: (record: TimeRecord) =>
        Math.round((record.totalMinutes || 0) / 60 * 100) / 100,
    },
    category: {
      header: 'カテゴリ',
      getValue: (record: TimeRecord) => (record.categories || []).join('; '),
    },
    memo: {
      header: '備考',
      getValue: (record: TimeRecord) => record.memo || record.notes || '',
    },
  };

  // 選択された列のみ使用（指定がない場合は全列）
  const columnsToExport = selectedColumns?.length
    ? selectedColumns.filter((key) => key in allColumns)
    : Object.keys(allColumns);

  // ヘッダー行を作成
  const headers = columnsToExport.map(
    (key) => allColumns[key as keyof typeof allColumns].header
  );

  // データ行を作成
  const rows = records.map((record) =>
    columnsToExport.map((key) =>
      allColumns[key as keyof typeof allColumns].getValue(record)
    )
  );

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
  ].join('\n');

  const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * 時間フォーマット（HH:MM）
 */
export function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}:${mins.toString().padStart(2, '0')}`;
}
